import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { AuthHeading, AuthLayout } from './AuthLayout';
import { Button } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { FormAlert } from '@/components/ui/Feedback';
import { api, ApiError } from '@/lib/api';
import { NewPasswordFields } from './NewPasswordFields';
import { canSubmitPassword } from './passwordRules';

/** Obscure the local part: `abdul@paracon.com.au` → `a•••••@paracon.com.au`. */
function maskEmail(email: string): string {
  const [local, domain] = email.split('@');
  if (!local || !domain) return email;
  return `${local.slice(0, 1)}${'•'.repeat(Math.max(local.length - 1, 3))}@${domain}`;
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get('token') ?? '';
  const email = params.get('email') ?? '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const ready = canSubmitPassword(password, confirmation);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      await api.post(
        '/auth/reset-password',
        { token, email, password, password_confirmation: confirmation },
        { anonymous: true },
      );
      setDone(true);
      window.setTimeout(() => navigate('/login', { replace: true }), 2200);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not reset your password. Request a new link and try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <AuthLayout>
        <AuthHeading
          eyebrow="Password updated"
          title="You're all set"
          description="Your password has been changed. Redirecting you to sign in…"
        />
        <div className="flex items-center gap-3 rounded-lg border border-signal-200 bg-signal-50 px-4 py-3.5">
          <CheckCircle2 aria-hidden className="h-5 w-5 shrink-0 text-signal" />
          <p className="text-sm font-medium text-signal-700">Password successfully updated</p>
        </div>
        <Link
          to="/login"
          className="mt-6 block text-center text-sm font-semibold text-digital transition-colors hover:text-digital-600"
        >
          Continue to sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <AuthHeading
        eyebrow="Secure password reset"
        title="Create a new password"
        description="Choose a strong password that you have not previously used for this account."
      />

      <form onSubmit={onSubmit} noValidate className="space-y-5">
        {error && <FormAlert>{error}</FormAlert>}

        {email && (
          <div className="flex items-center gap-3 rounded-lg bg-cloud px-3 py-2.5">
            <Avatar name={email} size="sm" tone="bg-navy text-white" />
            <span className="truncate text-sm text-body">{maskEmail(email)}</span>
          </div>
        )}

        <NewPasswordFields
          password={password}
          confirmation={confirmation}
          onPassword={setPassword}
          onConfirmation={setConfirmation}
        />

        <Button type="submit" size="lg" fullWidth disabled={!ready} loading={submitting}>
          {submitting ? 'Updating…' : 'Update password'}
        </Button>
      </form>

      <Link
        to="/login"
        className="mt-4 block text-center text-sm font-semibold text-digital transition-colors hover:text-digital-600"
      >
        Cancel and return to sign in
      </Link>
    </AuthLayout>
  );
}
